import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { playlists, Playlist } from '@/data/playlists';
import { rooms } from '@/data/rooms';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';

interface PlaylistProgress {
  playlistId: string;
  currentIndex: number;
  completedRooms: string[];
  startedAt: number;
}

const STORAGE_KEY = 'playlist-progress';

export const usePlaylist = () => {
  const navigate = useNavigate();
  const [activePlaylist, setActivePlaylist] = useState<PlaylistProgress | null>(() => {
    return safeGetJSON<PlaylistProgress | null>(STORAGE_KEY, null);
  });

  const startPlaylist = useCallback((playlistId: string) => {
    const playlist = playlists.find(p => p.id === playlistId);
    if (!playlist) return;

    const progress: PlaylistProgress = {
      playlistId,
      currentIndex: 0,
      completedRooms: [],
      startedAt: Date.now(),
    };
    safeSetJSON(STORAGE_KEY, progress);
    setActivePlaylist(progress);

    // Navigate to first room
    const firstRoom = rooms.find(r => r.id === playlist.rooms[0]);
    if (firstRoom) navigate(firstRoom.path);
  }, [navigate]);

  const completeCurrentRoom = useCallback(() => {
    if (!activePlaylist) return;

    const playlist = playlists.find(p => p.id === activePlaylist.playlistId);
    if (!playlist) return;

    const currentRoomId = playlist.rooms[activePlaylist.currentIndex];
    const nextIndex = activePlaylist.currentIndex + 1;

    if (nextIndex >= playlist.rooms.length) {
      // Playlist complete
      safeSetJSON(STORAGE_KEY, null);
      setActivePlaylist(null);
      return { completed: true, nextRoom: null };
    }

    const updated: PlaylistProgress = {
      ...activePlaylist,
      currentIndex: nextIndex,
      completedRooms: [...activePlaylist.completedRooms, currentRoomId],
    };
    safeSetJSON(STORAGE_KEY, updated);
    setActivePlaylist(updated);

    const nextRoom = rooms.find(r => r.id === playlist.rooms[nextIndex]);
    if (nextRoom) navigate(nextRoom.path);
    return { completed: false, nextRoom };
  }, [activePlaylist, navigate]);

  const exitPlaylist = useCallback(() => {
    safeSetJSON(STORAGE_KEY, null);
    setActivePlaylist(null);
    navigate('/hallway');
  }, [navigate]);

  const getActivePlaylistInfo = useCallback((): { playlist: Playlist; progress: PlaylistProgress } | null => {
    if (!activePlaylist) return null;
    const playlist = playlists.find(p => p.id === activePlaylist.playlistId);
    if (!playlist) return null;
    return { playlist, progress: activePlaylist };
  }, [activePlaylist]);

  return {
    playlists,
    activePlaylist,
    startPlaylist,
    completeCurrentRoom,
    exitPlaylist,
    getActivePlaylistInfo,
  };
};
